import { useEffect, useRef, useState } from 'react';
import { useChatStore } from '../stores/chatStore';
import { useAuthStore } from '../stores/authStore';
import { ConversationList } from '../components/ConversationList';
import { MessageList } from '../components/MessageList';
import { MessageInput } from '../components/MessageInput';
import { EmptyState } from '../components/EmptyState';
import { Toast } from '../components/Toast';

export function ChatPage() {
  const {
    conversations,
    currentConversationId,
    messages,
    isLoading,
    isStreaming,
    error,
    fetchConversations,
    createConversation,
    selectConversation,
    sendMessage,
    clearError,
  } = useChatStore();

  const { token } = useAuthStore();
  const [showConversations, setShowConversations] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (token) fetchConversations();
  }, [token, fetchConversations]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isStreaming]);

  const handleNewConversation = async () => {
    const id = await createConversation();
    await selectConversation(id);
    setShowConversations(false);
  };

  if (!token) return <EmptyState title="Configure seu token" description="Vá em Configurações para definir seu token de acesso." />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, overflow: 'hidden' }}>
      {error && <Toast message={error} onClose={clearError} />}

      <div style={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <aside
          className={showConversations ? 'open' : ''}
          style={{
            width: '280px',
            maxWidth: '85vw',
            background: 'var(--bg-elevated)',
            borderRight: '1px solid var(--border)',
            display: 'flex',
            flexDirection: 'column',
            position: 'fixed',
            left: 0,
            top: 0,
            bottom: 0,
            zIndex: 50,
            transform: showConversations ? 'translateX(0)' : 'translateX(-100%)',
            transition: 'transform 0.2s ease',
          }}
        >
          <div style={{ padding: '1rem', borderBottom: '1px solid var(--border)' }}>
            <h3 style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--fg-muted)', marginBottom: '0.5rem' }}>
              Conversas
            </h3>
            <button className="btn btn-primary btn-sm" style={{ width: '100%' }} onClick={handleNewConversation}>
              + Nova conversa
            </button>
          </div>
          <ConversationList
            conversations={conversations}
            currentId={currentConversationId}
            onSelect={selectConversation}
            onClose={() => setShowConversations(false)}
          />
        </aside>

        {showConversations && (
          <div
            className="sidebar-backdrop"
            onClick={() => setShowConversations(false)}
            style={{ position: 'fixed', inset: 0, zIndex: 40, background: 'rgba(0,0,0,0.3)' }}
          />
        )}

        <main
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            minHeight: 0,
            overflow: 'hidden',
            transition: 'margin-left 0.2s ease',
          }}
        >
          <header style={{
            flexShrink: 0,
            padding: '1rem',
            borderBottom: '1px solid var(--border)',
            background: 'var(--bg-elevated)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => setShowConversations(true)}
                style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}
              >
                ☰ Conversas
              </button>
              <div style={{ flex: 1 }}>
                <h2 style={{ fontSize: '1rem', fontWeight: 600 }}>
                  {conversations.find(c => c.id === currentConversationId)?.title || 'Nova conversa'}
                </h2>
                <p style={{ fontSize: '0.75rem', color: 'var(--fg-muted)' }}>
                  {currentConversationId ? `${messages.length} mensagens` : 'Selecione ou crie uma conversa'}
                </p>
              </div>
            </div>
          </header>

          <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '1rem' }}>
            {currentConversationId ? (
              <MessageList messages={messages} isStreaming={isStreaming} />
            ) : (
              <EmptyState
                title="Bem-vindo ao Agente Gerencial"
                description="Selecione uma conversa ao lado ou crie uma nova para começar."
                icon="💬"
              />
            )}
            <div ref={messagesEndRef} />
          </div>

          {currentConversationId && (
            <MessageInput onSend={sendMessage} disabled={isLoading || isStreaming} />
          )}
        </main>
      </div>
    </div>
  );
}
